import React, {useState} from 'react';
import States from 'ApplicationEntry/models/States';
import {maskPhoneNumber, unmaskPhoneNumber} from 'Shared/utilities/phoneNumberMasker';
import {maskTaxId, unMaskTaxId} from 'Shared/utilities/taxIdMasker';
import {
  Button,
  createMuiTheme,
  FormControl,
  Grid,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  ThemeProvider,
  Typography,
  Divider,
} from '@material-ui/core';
import {red} from '@material-ui/core/colors';
import {KeyboardDatePicker} from '@material-ui/pickers';
import {ApplicantType} from 'ApplicationEntry/models/ApplicantType';
import {Dependents, relations} from 'ApplicationEntry/models/Dependents';
import {isValidPhoneNumber, PhoneNumber, ValidPhoneNumber} from 'ApplicationEntry/models/PhoneNumber';
import {Sex} from 'ApplicationEntry/models/Sex';
import DateUtility from 'Shared/utilities/DateUtility';
import {isValidSocialSecurityNumber} from 'ApplicationEntry/models/SocialSecurityNumber';
import {isValidZipCode, maskZipCode, unmaskZipCode, ValidZipCode, ZipCode} from 'ApplicationEntry/models/ZipCode';
import * as ErrorMessages from '../ErrorMessages';
import { member } from 'fp-ts/lib/Map';

const theme = createMuiTheme({
  palette: {
    secondary: red,
  },
});

type DependentProps = {
  index: number;
  key: number;
  DependentIndex: number;
  editDependent: boolean;
  removeDependent: () => void;
  changeFirstName: (value: string) => void;
  changeMiddleName: (value: string) => void;
  changeLastName: (value: string) => void;
  changeRelationship: (value: string) => void;
  changeDOB: (value: Date | '') => void;
  changeSSN: (value: string) => void;
  changeSex: (value: string) => void;
  changeAddress: (value: string) => void;
  changeAddressLine2: (value: string) => void;
  changeCity: (value: string) => void;
  changeState: (value: string) => void;
  changeZip: (value: ZipCode) => void;
  changePhone: (value: PhoneNumber) => void;
  members: Dependents;
  dependentListLength: number;
};

const isInvalidDate = (date: Date) => isNaN(date.getTime());
const getSSNErrorMessage = () => ErrorMessages.INVALID_SSN_FORMAT;
const getPhoneErrorMessage = () => ErrorMessages.INVALID_PHONE_FORMAT;
const getZipErrorMessage = () => ErrorMessages.INVALID_ZIP_FORMAT;

const Dependent = (props: DependentProps) => {
  const {
    members,
    changeAddress,
    changeAddressLine2,
    changeCity,
    changeDOB,
    changeFirstName,
    changeLastName,
    changeMiddleName,
    changeSex,
    changeRelationship,
    changeSSN,
    changeState,
    changeZip,
    changePhone,
  } = props;
  const [phoneBlurred, setBlurPhone] = useState(false);
  const [ZipBlurred, setBlurZip] = useState(false);

  const [stateLabel, setStateLabel] = useState<HTMLLabelElement | null>();
  const [relationshipLabel, setRelationshipLabel] = useState<HTMLLabelElement | null>();
  const [sexLabel, setSexLabel] = useState<HTMLLabelElement | null>();

  const showDobError = members.dob !== '' && isInvalidDate(members.dob);
  const showSSNError =
    members.socialSecurityNumber.value !== '' && !isValidSocialSecurityNumber(members.socialSecurityNumber);
  const showPhoneError = phoneBlurred && members.phone.phoneNumber !== '' && !isValidPhoneNumber(members.phone);
  const showZipError = ZipBlurred && members.zip.value !== '' && !isValidZipCode(members.zip);

  const handleDependent = () => {
    props.removeDependent();
  };

  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);

  return (
    <div className="dependents-section__dependent-view" id="dependent-view">
      <div className="dependents-section__dependent-view__sub-title-container">
        <Typography variant="subtitle1" className="dependents-section__dependent-view__sub-title-container-title">
          Dependent {props.editDependent ? props.DependentIndex + 1 : props.dependentListLength + props.index + 1}
        </Typography>

        {props.index !== 0 ? (
          <ThemeProvider theme={theme}>
            <Button
              variant="contained"
              color="secondary"
              onClick={handleDependent}
              className="dependents-section__dependent-view-delete-btn"
            >
              Remove Dependent
            </Button>
          </ThemeProvider>
        ) : null}
      </div>

      <form noValidate autoComplete="off" className="dependents-section__dependent-view-form">
        <div className="dependents-section__dependent-view-form-row">
          <Grid container spacing={4}>
            <Grid item xs={12} md={3}>
              <TextField
                id="dependent-first-name"
                label="First Name"
                variant="outlined"
                placeholder="Enter First Name"
                margin="normal"
                fullWidth
                className="dependents-section__dependent-view-form-text-field"
                onChange={e => changeFirstName(e.target.value)}
                value={members.firstName.value}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                id="dependent-middle-name"
                label="Middle Name"
                variant="outlined"
                placeholder="Enter Middle Name"
                margin="normal"
                fullWidth
                className="dependents-section__dependent-view-form-text-field"
                value={members.middleName.value}
                onChange={e => changeMiddleName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                id="dependent-last-name"
                label="Last Name"
                variant="outlined"
                placeholder="Enter Last Name"
                margin="normal"
                fullWidth
                className="dependents-section__dependent-view-form-text-field"
                value={members.lastName.value}
                onChange={e => changeLastName(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" className="dependents-section__dependent-view-form-text-field">
                <InputLabel ref={setSexLabel} id="dependent-sex-label">
                  Sex
                </InputLabel>
                <Select
                  labelId="dependent-label-sex"
                  labelWidth={sexLabel?.clientWidth}
                  id="dependent-sex"
                  value={members.sex}
                  onChange={e => changeSex(e.target.value as Sex)}
                  displayEmpty
                >
                  <MenuItem value="MALE">Male</MenuItem>
                  <MenuItem value="FEMALE">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="dependent-ssn"
                label="SSN"
                variant="outlined"
                placeholder="XXX-XX-XXXX"
                inputProps={{
                  maxLength: 11,
                }}
                className="dependents-section__dependent-view-form-text-field"
                autoComplete="off"
                error={showSSNError}
                helperText={showSSNError ? getSSNErrorMessage() : ''}
                value={maskTaxId(members.socialSecurityNumber.value)}
                onChange={e => changeSSN(unMaskTaxId(e.target.value))}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" className="dependents-section__dependent-view-form-text-field">
                <InputLabel ref={setRelationshipLabel} id="dependent-relationship-label">
                  Relationship
                </InputLabel>
                <Select
                  labelId="dependent-label-relationship"
                  labelWidth={relationshipLabel?.clientWidth}
                  id="dependent-relationship"
                  value={members.relationship.value}
                  onChange={e => changeRelationship(e.target.value as ApplicantType)}
                  displayEmpty
                >
                  {relations.map(rel => (
                    <MenuItem value={rel} key={rel}>
                      {rel}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} md={3}>
              <KeyboardDatePicker
                inputVariant="outlined"
                label="Date of Birth"
                format="MM/dd/yyyy"
                variant="inline"
                className="dependents-section__dependent-view-form-text-field"
                value={members.dob || null}
                error={showDobError}
                helperText={showDobError ? ErrorMessages.INVALID_DATE : ''}
                onChange={date => changeDOB(date?.toJSDate() || '')}
                maxDate={yesterday}
                autoOk={true}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
               className="dependents-section__dependent-view-form-text-field"
               id="age"
               inputProps={{readOnly: true}}
               label="Age"
               placeholder="Age"
               value={members.dob ? DateUtility.getAgeFromDob(members.dob) : 0}
               variant="filled"
              />
           </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                id="dependent-phone-number"
                label="Phone Number"
                variant="outlined"
                placeholder="(XXX)-XXX XXXX"
                inputProps={{
                  maxLength: 14,
                }}
                value={maskPhoneNumber(members.phone.phoneNumber)}
                error={showPhoneError}
                helperText={showPhoneError ? getPhoneErrorMessage() : ''}
                onBlur={() => setBlurPhone(true)}
                onChange={e => changePhone(ValidPhoneNumber.create(unmaskPhoneNumber(e.target.value)))}
                className="dependents-section__dependent-view-form-text-field"
              />
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="dependent-address"
                label="Address"
                variant="outlined"
                placeholder="Enter Address"
                margin="normal"
                fullWidth
                className="dependents-section__dependent-view-form-text-field"
                value={members.address.value}
                onChange={e => changeAddress(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                id="dependent-address-line2"
                label="Address Line 2"
                variant="outlined"
                placeholder="Enter Address Line 2"
                margin="normal"
                fullWidth
                className="dependents-section__dependent-view-form-text-field"
                value={members.addressLine2}
                onChange={e => changeAddressLine2(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <TextField
                id="dependent-city"
                label="City"
                variant="outlined"
                placeholder="Enter City"
                margin="normal"
                fullWidth
                className="dependents-section__dependent-view-form-text-field"
                value={members.city.value}
                onChange={e => changeCity(e.target.value)}
              />
            </Grid>
            <Grid item xs={12} md={3}>
              <FormControl variant="outlined" className="dependents-section__dependent-view-form-text-field">
                <InputLabel ref={setStateLabel} id="dependent-state">
                  State
                </InputLabel>
                <Select
                  labelId="label-dependent-state"
                  labelWidth={stateLabel?.clientWidth}
                  id="select-dependent-state"
                  value={members.state.value}
                  onChange={e => changeState(e.target.value as ApplicantType)}
                  displayEmpty
                >
                  {States.map(stateCode => (
                    <MenuItem value={stateCode} key={stateCode}>
                      {stateCode}
                    </MenuItem>
                  ))}
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12} md={3}>
              <TextField
                id="dependent-zip"
                label="Zip"
                variant="outlined"
                placeholder="XXXXX-XXXX"
                inputProps={{
                  maxLength: 10,
                }}
                className="dependents-section__dependent-view-form-text-field"
                error={showZipError}
                helperText={showZipError ? getZipErrorMessage() : ''}
                onBlur={() => setBlurZip(true)}
                value={maskZipCode(members.zip.value)}
                onChange={e => changeZip(ValidZipCode.create(unmaskZipCode(e.target.value)))}
              />
            </Grid>
          </Grid>
        </div>
      </form>
      <Divider className="application-form__actions-divider" />
    </div>
  );
};

export default Dependent;
